<template>
  <div class="ic-overlay">
    <div class="ic-modal">

      <div class="ic-header">
        <span class="ic-title">{{ tr.title }}</span>
        <div class="ic-actions">
          <button class="ic-btn-cancel" @click="$emit('cancel')">{{ tr.cancel }}</button>
          <button class="ic-btn-save" @click="save">{{ tr.save }}</button>
        </div>
      </div>

      <div class="ic-hint">{{ tr.hint }}</div>

      <div class="ic-canvas-wrap">
        <canvas
          ref="canvas"
          class="ic-canvas"
          :style="{ cursor: cursorStyle }"
          @mousedown="onDown"
          @mousemove="onMove"
          @mouseup="onUp"
          @mouseleave="onUp"
          @touchstart.prevent="onTouchDown"
          @touchmove.prevent="onTouchMove"
          @touchend.prevent="onUp"
        ></canvas>
      </div>

    </div>
  </div>
</template>

<script>
/* eslint-disable */
const MIN_SIZE = 40; // minimum crop dimension in image pixels

export default {
  name: 'ImageCropper',
  props: {
    src:    { type: String, required: true },
    lang:   { type: String, default: 'fr' },
    square: { type: Boolean, default: false },
  },
  emits: ['save', 'cancel'],

  data() {
    return {
      img: null,
      cropX: 0,
      cropY: 0,
      cropW: 0,
      cropH: 0,
      // interaction state
      mode: null,       // null | 'move' | 'tl' | 'tr' | 'bl' | 'br'
      dragStartX: 0,    // image pixel where drag started
      dragStartY: 0,
      dragStartCrop: null, // snapshot of crop at drag start
      hoverMode: null,  // for cursor
    };
  },

  computed: {
    tr() {
      const map = {
        fr: {
          title: this.square ? '✂️ Recadrer (carré)' : '✂️ Recadrer l\'image',
          cancel: 'Annuler', save: '✓ Sauvegarder',
          hint: this.square
            ? 'Cadre carré · Glisse l\'intérieur pour déplacer · Glisse les coins pour redimensionner'
            : 'Glisse l\'intérieur du cadre pour le déplacer · Glisse les coins pour redimensionner',
        },
        en: {
          title: this.square ? '✂️ Crop (square)' : '✂️ Crop image',
          cancel: 'Cancel', save: '✓ Save',
          hint: this.square
            ? 'Square frame · Drag inside to move · Drag corners to resize'
            : 'Drag inside the frame to move it · Drag the corners to resize',
        },
        he: {
          title: this.square ? '✂️ חיתוך (ריבוע)' : '✂️ חיתוך תמונה',
          cancel: 'ביטול', save: '✓ שמור',
          hint: this.square
            ? 'מסגרת ריבועית · גרור פנימה להזזה · גרור פינות לשינוי גודל'
            : 'גרור בתוך המסגרת להזזה · גרור את הפינות לשינוי גודל',
        },
      };
      return map[this.lang] || map.fr;
    },
    cursorStyle() {
      switch (this.hoverMode) {
        case 'move': return 'move';
        case 'tl':
        case 'br': return 'nwse-resize';
        case 'tr':
        case 'bl': return 'nesw-resize';
        default:    return 'default';
      }
    },
  },

  mounted() {
    const img = new Image();
    img.onload = () => {
      this.img = img;
      const canvas = this.$refs.canvas;
      canvas.width  = img.width;
      canvas.height = img.height;
      // Initial crop: square (1:1) or 4:3 centered
      const targetRatio = this.square ? 1 : 4 / 3;
      if (img.width / img.height >= targetRatio) {
        this.cropH = img.height;
        this.cropW = img.height * targetRatio;
      } else {
        this.cropW = img.width;
        this.cropH = img.width / targetRatio;
      }
      this.cropX = (img.width  - this.cropW) / 2;
      this.cropY = (img.height - this.cropH) / 2;
      this.draw();
    };
    img.src = this.src;
  },

  methods: {

    /* ── coordinate helpers ── */
    getPos(clientX, clientY) {
      const rect  = this.$refs.canvas.getBoundingClientRect();
      const scaleX = this.$refs.canvas.width  / rect.width;
      const scaleY = this.$refs.canvas.height / rect.height;
      return {
        x: (clientX - rect.left) * scaleX,
        y: (clientY - rect.top)  * scaleY,
      };
    },

    // Returns handle name ('tl','tr','bl','br') if (x,y) is near a corner, else null
    hitHandle(x, y) {
      const { cropX: cx, cropY: cy, cropW: cw, cropH: ch } = this;
      const rect  = this.$refs.canvas.getBoundingClientRect();
      const scale = this.$refs.canvas.width / rect.width;
      const thr   = 28 * scale; // 28 screen-px hit zone
      const corners = [
        { name: 'tl', px: cx,      py: cy       },
        { name: 'tr', px: cx + cw, py: cy       },
        { name: 'bl', px: cx,      py: cy + ch  },
        { name: 'br', px: cx + cw, py: cy + ch  },
      ];
      for (const c of corners) {
        if (Math.abs(x - c.px) <= thr && Math.abs(y - c.py) <= thr) return c.name;
      }
      return null;
    },

    insideCrop(x, y) {
      return x >= this.cropX && x <= this.cropX + this.cropW &&
             y >= this.cropY && y <= this.cropY + this.cropH;
    },

    /* ── pointer events ── */
    onDown(e) {
      const { x, y } = this.getPos(e.clientX, e.clientY);
      const handle = this.hitHandle(x, y);
      if (handle) {
        this.mode = handle;
      } else if (this.insideCrop(x, y)) {
        this.mode = 'move';
      } else {
        return;
      }
      this.dragStartX    = x;
      this.dragStartY    = y;
      this.dragStartCrop = { x: this.cropX, y: this.cropY, w: this.cropW, h: this.cropH };
    },

    onMove(e) {
      const { x, y } = this.getPos(e.clientX, e.clientY);

      // Update hover cursor
      const handle = this.hitHandle(x, y);
      if (handle) {
        this.hoverMode = handle;
      } else if (this.insideCrop(x, y)) {
        this.hoverMode = 'move';
      } else {
        this.hoverMode = null;
      }

      if (!this.mode) return;

      const dx = x - this.dragStartX;
      const dy = y - this.dragStartY;
      const { x: ox, y: oy, w: ow, h: oh } = this.dragStartCrop;
      const W = this.img.width;
      const H = this.img.height;

      const RATIO = this.square ? 1 : 4 / 3; // W = H * RATIO

      if (this.mode === 'move') {
        this.cropX = Math.max(0, Math.min(ox + dx, W - this.cropW));
        this.cropY = Math.max(0, Math.min(oy + dy, H - this.cropH));

      } else if (this.mode === 'br') {
        let newW = Math.max(MIN_SIZE, Math.min(ow + dx, W - ox));
        let newH = newW / RATIO;
        if (oy + newH > H) { newH = H - oy; newW = newH * RATIO; }
        this.cropW = newW; this.cropH = newH;

      } else if (this.mode === 'bl') {
        // right edge fixed at ox + ow
        let newW = Math.max(MIN_SIZE, Math.min(ow - dx, ox + ow));
        let newH = newW / RATIO;
        if (oy + newH > H) { newH = H - oy; newW = newH * RATIO; }
        this.cropX = ox + ow - newW;
        this.cropW = newW; this.cropH = newH;

      } else if (this.mode === 'tr') {
        // bottom edge fixed at oy + oh
        let newW = Math.max(MIN_SIZE, Math.min(ow + dx, W - ox));
        let newH = newW / RATIO;
        const newY = oy + oh - newH;
        if (newY < 0) { newH = oy + oh; newW = newH * RATIO; }
        this.cropW = newW; this.cropH = newH;
        this.cropY = Math.max(0, oy + oh - newH);

      } else if (this.mode === 'tl') {
        // right + bottom edges fixed
        let newW = Math.max(MIN_SIZE, Math.min(ow - dx, ox + ow));
        let newH = newW / RATIO;
        const newY = oy + oh - newH;
        if (newY < 0) { newH = oy + oh; newW = newH * RATIO; }
        this.cropX = ox + ow - newW;
        this.cropY = Math.max(0, oy + oh - newH);
        this.cropW = newW; this.cropH = newH;
      }

      this.draw();
    },

    onUp() {
      this.mode = null;
    },

    onTouchDown(e) {
      const t = e.touches[0];
      this.onDown({ clientX: t.clientX, clientY: t.clientY });
    },

    onTouchMove(e) {
      const t = e.touches[0];
      this.onMove({ clientX: t.clientX, clientY: t.clientY });
    },

    /* ── canvas drawing ── */
    draw() {
      const canvas = this.$refs.canvas;
      const ctx    = canvas.getContext('2d');
      const W = canvas.width;
      const H = canvas.height;
      const { cropX: cx, cropY: cy, cropW: cw, cropH: ch } = this;

      // 1. Full image
      ctx.drawImage(this.img, 0, 0, W, H);

      // 2. Dark overlay everywhere
      ctx.fillStyle = 'rgba(0,0,0,0.52)';
      ctx.fillRect(0, 0, W, H);

      // 3. Redraw image inside crop rect (punches through overlay)
      ctx.save();
      ctx.beginPath();
      ctx.rect(cx, cy, cw, ch);
      ctx.clip();
      ctx.drawImage(this.img, 0, 0, W, H);
      ctx.restore();

      // 4. Crop border
      const lw = Math.max(3, W / 300);
      ctx.strokeStyle = 'white';
      ctx.lineWidth   = lw;
      ctx.strokeRect(cx, cy, cw, ch);

      // 5. Rule-of-thirds grid
      ctx.strokeStyle = 'rgba(255,255,255,0.3)';
      ctx.lineWidth   = Math.max(1, W / 600);
      for (let i = 1; i <= 2; i++) {
        ctx.beginPath(); ctx.moveTo(cx + cw / 3 * i, cy); ctx.lineTo(cx + cw / 3 * i, cy + ch); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(cx, cy + ch / 3 * i); ctx.lineTo(cx + cw, cy + ch / 3 * i); ctx.stroke();
      }

      // 6. Corner handles — filled squares with dark outline
      const hs = Math.max(14, W / 50);
      const corners = [
        { px: cx,      py: cy       },
        { px: cx + cw, py: cy       },
        { px: cx,      py: cy + ch  },
        { px: cx + cw, py: cy + ch  },
      ];
      corners.forEach(({ px, py }) => {
        ctx.fillStyle   = 'white';
        ctx.fillRect(px - hs / 2, py - hs / 2, hs, hs);
        ctx.strokeStyle = 'rgba(0,0,0,0.5)';
        ctx.lineWidth   = lw * 0.5;
        ctx.strokeRect(px - hs / 2, py - hs / 2, hs, hs);
      });

      // 7. Dimensions label
      ctx.fillStyle = 'rgba(0,0,0,0.55)';
      const labelW = 130, labelH = 26;
      const labelX = cx + (cw - labelW) / 2;
      const labelY = cy + ch + lw * 2;
      if (labelY + labelH < H) {
        ctx.beginPath();
        ctx.roundRect(labelX, labelY, labelW, labelH, 6);
        ctx.fill();
        ctx.fillStyle = 'white';
        ctx.font = `bold ${Math.max(14, W / 60)}px system-ui`;
        ctx.textAlign = 'center';
        ctx.fillText(`${Math.round(cw)} × ${Math.round(ch)}`, cx + cw / 2, labelY + 18);
      }
    },

    /* ── save ── */
    save() {
      const out = document.createElement('canvas');
      out.width  = Math.round(this.cropW);
      out.height = Math.round(this.cropH);
      out.getContext('2d').drawImage(
        this.img,
        this.cropX, this.cropY, this.cropW, this.cropH,
        0, 0, out.width, out.height
      );
      this.$emit('save', out.toDataURL('image/jpeg', 0.92));
    },
  },
};
</script>

<style scoped>
.ic-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.9);
  z-index: 25000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}

.ic-modal {
  background: #1e1e2e;
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  max-width: min(96vw, 920px);
  max-height: 95vh;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.7);
}

.ic-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: #2a2a3e;
  border-bottom: 1px solid rgba(255,255,255,0.08);
  flex-wrap: wrap;
  gap: 10px;
}

.ic-title {
  color: #eee;
  font-size: 15px;
  font-weight: 700;
  font-family: system-ui, sans-serif;
}

.ic-actions { display: flex; gap: 8px; }

.ic-hint {
  text-align: center;
  color: rgba(255,255,255,0.5);
  font-size: 12px;
  padding: 8px 16px 4px;
  font-family: system-ui, sans-serif;
}

.ic-btn-cancel {
  padding: 8px 16px;
  border-radius: 8px;
  border: 1.5px solid rgba(255,255,255,0.2);
  background: transparent;
  color: #aaa;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}
.ic-btn-cancel:hover { background: rgba(255,255,255,0.08); }

.ic-btn-save {
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: #22c55e;
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
}
.ic-btn-save:hover { background: #16a34a; }

.ic-canvas-wrap {
  overflow: auto;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #111;
  padding: 8px;
}

.ic-canvas {
  max-width: 100%;
  max-height: calc(95vh - 108px);
  display: block;
  touch-action: none;
  border-radius: 4px;
}
</style>
